import { useEffect, useState } from 'react';
import { applyBodyClass, readPreference } from './usePreferences';

// Theme preference with three modes: 'light' | 'dark' | 'system'.
//
// The persisted *preference* is one of the three values above; the
// *body class* is always `theme-light` or `theme-dark`. When the user
// picks 'system' we resolve at runtime via `prefers-color-scheme` and
// keep listening for OS-level changes so the app re-flips when the
// user toggles their OS theme without reloading.
//
// FOUC prevention runs at module load before React mounts so the very
// first paint already has the correct palette.

const STORAGE_KEY = 'toleranz.theme';
const VALID = new Set(['light', 'dark', 'system']);
const RESOLVED_VALID = new Set(['light', 'dark']);
const PREFIX = 'theme-';
const DEFAULT_PREF = 'system';

function systemPrefersDark() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

/** Map a stored preference to the actual body-class value. */
function resolve(preference) {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

// FOUC: pick + apply the initial body class before React mounts.
if (typeof document !== 'undefined') {
  const initialPref = readPreference(STORAGE_KEY, VALID, DEFAULT_PREF);
  applyBodyClass(PREFIX, RESOLVED_VALID, resolve(initialPref));
}

export function useTheme() {
  const [preference, setPreference] = useState(() =>
    readPreference(STORAGE_KEY, VALID, DEFAULT_PREF)
  );

  // Reflect + persist on every preference change.
  useEffect(() => {
    applyBodyClass(PREFIX, RESOLVED_VALID, resolve(preference));
    try { window.localStorage?.setItem(STORAGE_KEY, preference); } catch { /* ignore */ }
  }, [preference]);

  // When the user has picked 'system', subscribe to OS-level changes
  // so the body class flips when they change their OS theme. The
  // listener is scoped to the 'system' case so explicit light/dark
  // picks aren't surprise-overridden by the OS.
  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => applyBodyClass(PREFIX, RESOLVED_VALID, mq.matches ? 'dark' : 'light');
    // Some older browsers expose addListener instead of addEventListener.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener?.(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener?.(onChange);
    };
  }, [preference]);

  // Cross-tab sync — pick up changes made in other tabs/windows.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && VALID.has(e.newValue)) setPreference(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return [preference, setPreference];
}
